import React, { useState } from 'react';
import { Card, useTheme, Confirm, fmt, Btn } from './shared';
import { RotateCcw, Trash2, Archive, Filter } from 'lucide-react';
import { S } from './strings';

export default function ArchiveTab({ data, uOrg, apiSync }) {
  const t = useTheme();
  // Вкладки: accounts, funds, contractors, categories, tx
  const [subTab, setSubTab] = useState('accounts');

  const [confirmOp, setConfirmOp] = useState(null); 
  // R4 (2026-06-12): счёт для восстановления операции мёртвого счёта
  const [restoreTarget, setRestoreTarget] = useState('');
  // confirmOp = { type: 'restore' | 'delete', entityType: 'tx' | 'acc' | ..., item: obj }

  const arcAccounts = (data.accounts || []).filter(a => a.archived);
  const arcFunds = (data.funds || []).filter(f => f.archived);
  const arcContractors = (data.contractors || []).filter(c => c.archived);
  const arcInc = (data.incomeCategories || []).filter(c => c.archived);
  const arcExp = (data.expenseCategories || []).filter(c => c.archived);
  const arcTxs = (data.transactions || []).filter(tx => tx.archived);
  const arcDebts = (data.debts || []).filter(d => d.status === 'archived');

  // Восстановление — O7 (2026-06-10): sequential, await sync → reload-org (серверная правда).
  const executeRestore = async () => {
    const { entityType, item } = confirmOp;
    setConfirmOp(null);
    if (!apiSync) return;
    let r = null;
    if (entityType === 'tx') {
      // R4 (2026-06-12): операция мёртвого счёта восстанавливается с переносом на выбранный
      // живой счёт (backend guard RESTORE_ACCOUNT_ARCHIVED не пустит на архивный счёт).
      const _acc = (data.accounts || []).find(a => a.id === item.accountId);
      const payload = { archived: false };
      if (_acc && _acc.archived) {
        if (!restoreTarget) { alert('Нет живого счёта для восстановления операции.'); return; }
        payload.accountId = restoreTarget;
      }
      if (apiSync.syncUpdateOperation) r = await apiSync.syncUpdateOperation(item.id, payload);
    } else if (entityType === 'debts') {
      const paid = item.paidAmount || 0;
      const newStatus = paid <= 0.005 ? 'active' : (paid + 0.005 >= item.amount ? 'paid' : 'partial');
      if (apiSync.syncUpdateDebt) r = await apiSync.syncUpdateDebt(item.id, { status: newStatus });
    } else if (entityType === 'funds') {
      if (apiSync.syncUpdateFund) r = await apiSync.syncUpdateFund(item.id, { isArchived: false });
    } else if (entityType === 'accounts') {
      if (apiSync.syncUpdateAccount) r = await apiSync.syncUpdateAccount(item.id, { isArchived: false });
    } else if (entityType === 'contractors') {
      if (apiSync.syncUpdateContractor) r = await apiSync.syncUpdateContractor(item.id, { isArchived: false });
    } else if (entityType === 'incomeCategories' || entityType === 'expenseCategories') {
      if (apiSync.syncUpdateCategory) r = await apiSync.syncUpdateCategory(item.id, { isArchived: false });
    }
    if (!r) return;
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  // Окончательное удаление — O7 (2026-06-10): sequential, await sync → reload-org.
  const executeDelete = async () => {
    const { entityType, item } = confirmOp;
    setConfirmOp(null);
    if (!apiSync) return;
    let r = null;
    if (entityType === 'transactions') {
      // R4 (2026-06-12): guard «сначала восстановите счёт» убран — операции в этом списке
      // всегда archived, их эффект на балансы уже снят, backend DELETE откат не делает.
      if (apiSync.syncDeleteOperation) r = await apiSync.syncDeleteOperation(item.id);
    }
    else if (entityType === 'debts' && apiSync.syncDeleteDebt) r = await apiSync.syncDeleteDebt(item.id);
    else if (entityType === 'accounts' && apiSync.syncDeleteAccount) r = await apiSync.syncDeleteAccount(item.id);
    else if (entityType === 'funds' && apiSync.syncArchiveFund) r = await apiSync.syncArchiveFund(item.id);
    else if (entityType === 'contractors' && apiSync.syncDeleteContractor) r = await apiSync.syncDeleteContractor(item.id);
    else if ((entityType === 'incomeCategories' || entityType === 'expenseCategories') && apiSync.syncArchiveCategory) r = await apiSync.syncArchiveCategory(item.id);
    if (!r) return;
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  // Модалка подтверждения
  const renderConfirm = () => {
    if (!confirmOp) return null;
    const { type, entityType, item } = confirmOp;
    
    let msg = '';
    let desc = '';
    
    if (type === 'delete') {
      msg = S().archive.deleteForever;
      desc = S().archive.deleteForeverDesc;
    } else if (type === 'restore') {
      msg = S().archive.restoreFromArchive;
      if (entityType === 'tx') {
        const verb = item.type === 'income' ? S().archive.restoreTxCredited : S().archive.restoreTxDebited;
        desc = S().archive.restoreTxDesc(fmt(item.amount), verb);
      } else if (entityType === 'accounts' || entityType === 'funds') {
        desc = S().archive.restoreAccFundDesc(item.name);
      } else if (entityType === 'contractors') {
        desc = S().archive.restoreContractorDesc(item.name);
      } else if (entityType === 'debts') {
        desc = 'Обязательство вернётся в активные (статус пересчитается по оплаченному).';
      } else {
        desc = S().archive.restoreCategoryDesc(item.name);
      }
    }

    // R4: восстановление операции мёртвого счёта — выбор живого счёта прямо в подтверждении
    const _txDeadAcc = type === 'restore' && entityType === 'tx'
      ? (data.accounts || []).find(a => a.id === item.accountId)
      : null;
    const _txAccDead = !!(_txDeadAcc && _txDeadAcc.archived);
    const _liveAccs = (data.accounts || []).filter(a => !a.archived && !a.isHidden);

    return (
      <Confirm 
        msg={msg} 
        extra={<div style={{fontSize: 13, color: t.textMuted, marginBottom: 20, lineHeight: 1.5}}>
          {desc}
          {_txAccDead && (_liveAccs.length === 0
            ? <div style={{color: t.accentRed, marginTop: 8}}>Счёт операции удалён, а живых счетов нет — восстановление невозможно.</div>
            : <div style={{marginTop: 10}}>
                <div style={{color: t.accentOrange, marginBottom: 6}}>Счёт операции удалён. Выберите счёт: {item.type === 'income' ? 'на него зачислится' : 'с него спишется'} {fmt(item.amount)} ₸.</div>
                <select value={restoreTarget} onChange={e => setRestoreTarget(e.target.value)} style={{width: '100%', padding: '8px 10px', borderRadius: 8, background: t.bgCard, color: t.text, border: `1px solid ${t.border}`, fontSize: 13}}>
                  {_liveAccs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>)}
        </div>}
        onNo={() => setConfirmOp(null)} 
        onYes={type === 'restore' ? executeRestore : executeDelete} 
      />
    );
  };

  return (
    <div style={{}}>
      <div style={{display:'flex', gap:10, marginBottom:20, flexWrap:'wrap'}}>
        {[
          { id: 'accounts', label: S().archive.tabAccounts, count: arcAccounts.length },
          { id: 'funds', label: S().archive.tabFunds, count: arcFunds.length },
          { id: 'contractors', label: S().archive.tabContractors, count: arcContractors.length },
          { id: 'categories', label: S().archive.tabCategories, count: arcInc.length + arcExp.length },
          { id: 'tx', label: S().archive.tabTx, count: arcTxs.length },
          { id: 'debts', label: 'Обязательства', count: arcDebts.length },
        ].map(tab => (
          <button 
            key={tab.id}
            onClick={() => setSubTab(tab.id)} 
            style={{
              padding:'8px 16px', borderRadius:10, 
              background: subTab === tab.id ? t.accent : `${t.accent}15`, 
              color: subTab === tab.id ? '#fff' : t.accent, 
              fontWeight: 700, border:'none', cursor:'pointer', transition:'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            {tab.label} <span style={{ background: subTab === tab.id ? 'rgba(255,255,255,0.2)' : `${t.accent}20`, padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>{tab.count}</span>
          </button>
        ))}
      </div>

      <div style={{ display:'grid', gap:8 }}>
        {subTab === 'accounts' && (
          arcAccounts.length === 0 ? <EmptyArchive state={S().archive.emptyAccounts} t={t}/> : arcAccounts.map(a => <ArchiveItem key={a.id} entity={a} entityType="accounts" t={t} onRestore={()=>setConfirmOp({type:'restore', entityType:'accounts', item:a})} onDel={()=>setConfirmOp({type:'delete', entityType:'accounts', item:a})} />)
        )}

        {subTab === 'funds' && (
          arcFunds.length === 0 ? <EmptyArchive state={S().archive.emptyFunds} t={t}/> : arcFunds.map(f => <ArchiveItem key={f.id} entity={f} entityType="funds" t={t} onRestore={()=>setConfirmOp({type:'restore', entityType:'funds', item:f})} onDel={()=>setConfirmOp({type:'delete', entityType:'funds', item:f})} />)
        )}

        {subTab === 'contractors' && (
          arcContractors.length === 0 ? <EmptyArchive state={S().archive.emptyContractors} t={t}/> : arcContractors.map(c => <ArchiveItem key={c.id} entity={c} entityType="contractors" t={t} onRestore={()=>setConfirmOp({type:'restore', entityType:'contractors', item:c})} onDel={()=>setConfirmOp({type:'delete', entityType:'contractors', item:c})} />)
        )}

        {subTab === 'categories' && (
          (arcInc.length + arcExp.length === 0) ? <EmptyArchive state={S().archive.emptyCategories} t={t}/> : [...arcInc.map(c=>({...c,k:'incomeCategories',p:S().archive.incomePrefix})), ...arcExp.map(c=>({...c,k:'expenseCategories',p:S().archive.expensePrefix}))].map(c => (
            <ArchiveItem key={c.id} entity={{...c, name: c.p + c.name}} entityType={c.k} t={t} onRestore={()=>setConfirmOp({type:'restore', entityType:c.k, item:c})} onDel={()=>setConfirmOp({type:'delete', entityType:c.k, item:c})} />
          ))
        )}

        {subTab === 'tx' && (
          arcTxs.length === 0 ? <EmptyArchive state={S().archive.emptyTx} t={t}/> : arcTxs.map(tx => {
            const tc = tx.type==='income'?t.accentGreen:tx.type==='expense'?t.accentRed:t.accentCyan;
            const label = tx.type==='income'?S().archive.income:tx.type==='expense'?S().archive.expense:S().archive.transfer;
            const accN = data.accounts.find(a=>a.id===tx.accountId)?.name||'—';
            return (
              <div key={tx.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', background:t.bgInput, borderRadius:12, opacity:0.85, border:`1px solid ${t.border}`}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                    <span style={{fontSize:11,padding:'3px 10px',borderRadius:10,fontWeight:700,background:`${tc}14`,color:tc}}>{label}</span>
                    <span className="mono" style={{fontSize:16, fontWeight:800, color:tc}}>{tx.type==='income'?'+':'−'}{fmt(tx.amount)}</span>
                  </div>
                  <div style={{fontSize:12, color:t.textMuted, marginTop:6}}>💳 {accN} {tx.comment ? `· 💬 ${tx.comment}` : ''} {tx.contractorName ? `· 👤 ${tx.contractorName}` : ''}</div>
                </div>
                <div style={{display:'flex', gap:8, flexShrink:0}}>
                  <button onClick={()=>{ const la=(data.accounts||[]).filter(a=>!a.archived&&!a.isHidden); setRestoreTarget(la[0]?la[0].id:''); setConfirmOp({type:'restore', entityType:'tx', item:tx}); }} style={{padding: '8px 12px', borderRadius: 8, color:t.accentGreen, background:`${t.accentGreen}15`, fontSize:12, fontWeight:700, display:'flex', alignItems:'center', gap:6, border:'none', cursor:'pointer'}}><RotateCcw size={16}/> {S().archive.restore}</button>
                  <button onClick={()=>setConfirmOp({type:'delete', entityType:'transactions', item:tx})} style={{padding: '8px 12px', borderRadius: 8, color:t.accentRed, background:`${t.accentRed}15`, border:'none', cursor:'pointer'}}><Trash2 size={16}/></button>
                </div>
              </div>
            );
          })
        )}

        {subTab === 'debts' && (
          arcDebts.length === 0 ? <EmptyArchive state="обязательства" t={t}/> : arcDebts.map(d => {
            const tc = d.type === 'receivable' ? t.accentGreen : t.accentRed;
            const label = d.type === 'receivable' ? 'Нам должны' : 'Мы должны';
            const conN = (data.contractors || []).find(c => c.id === d.contractorId)?.name || '—';
            return (
              <div key={d.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', background:t.bgInput, borderRadius:12, opacity:0.85, border:`1px solid ${t.border}`}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                    <span style={{fontSize:11,padding:'3px 10px',borderRadius:10,fontWeight:700,background:`${tc}14`,color:tc}}>{label}</span>
                    <span className="mono" style={{fontSize:16, fontWeight:800, color:tc}}>{fmt(d.amount)}</span>
                  </div>
                  <div style={{fontSize:12, color:t.textMuted, marginTop:6}}>👤 {conN}{d.description ? ` · ${d.description}` : ''}</div>
                </div>
                <div style={{display:'flex', gap:8, flexShrink:0}}>
                  <button onClick={()=>setConfirmOp({type:'restore', entityType:'debts', item:d})} style={{padding: '8px 12px', borderRadius: 8, color:t.accentGreen, background:`${t.accentGreen}15`, fontSize:12, fontWeight:700, display:'flex', alignItems:'center', gap:6, border:'none', cursor:'pointer'}}><RotateCcw size={16}/> {S().archive.restore}</button>
                  <button onClick={()=>setConfirmOp({type:'delete', entityType:'debts', item:d})} style={{padding: '8px 12px', borderRadius: 8, color:t.accentRed, background:`${t.accentRed}15`, border:'none', cursor:'pointer'}}><Trash2 size={16}/></button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {renderConfirm()}
    </div>
  );
}

function ArchiveItem({entity, entityType, t, onRestore, onDel}) {
  return (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', background:t.bgInput, borderRadius:12, opacity:0.85, border:`1px solid ${t.border}`}}>
      <div>
        <div style={{fontWeight:700, fontSize: 14}}>{entity.name}</div>
        {(entityType === 'accounts' || entityType === 'funds') && <div style={{fontSize: 12, color: t.textMuted, marginTop: 4}}>{S().archive.balance} {fmt(entity.balance || 0)}</div>}
      </div>
      <div style={{display:'flex', gap:8, flexShrink:0}}>
        <button onClick={onRestore} style={{padding: '8px 12px', borderRadius: 8, color:t.accentGreen, background:`${t.accentGreen}15`, fontSize:12, fontWeight:700, display:'flex', alignItems:'center', gap:6, border:'none', cursor:'pointer'}}><RotateCcw size={16}/> {S().archive.restore}</button>
        <button onClick={onDel} style={{padding: '8px 12px', borderRadius: 8, color:t.accentRed, background:`${t.accentRed}15`, border:'none', cursor:'pointer'}}><Trash2 size={16}/></button>
      </div>
    </div>
  );
}

function EmptyArchive({state, t}) {
  return (
    <div style={{padding: 40, textAlign: 'center', background: t.bgInput, borderRadius: 12, border: `1px dashed ${t.border}`}}>
      <Archive size={32} color={t.textMuted} style={{marginBottom: 10, opacity: 0.5}} />
      <div style={{color: t.textMuted, fontSize: 14, fontWeight: 500}}>{S().archive.noArchived(state)}</div>
    </div>
  );
}
